import React, { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import "../../css/teacher/ChangePassword.css";

function ChangePassword() {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.currentPassword.trim()) {
            errors.currentPassword = "Current password is required";
        }
        if (!formData.newPassword.trim()) {
            errors.newPassword = "New password is required";
        } else if (formData.newPassword.length < 8) {
            errors.newPassword = "Password must be at least 8 characters";
        }
        if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);
        setFormErrors({});
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                setErrorMessage("Unauthorized. Please login again.");
                setIsSubmitting(false);
                return;
            }

            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/changePassword/change", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || "Failed to change password");
            }

            setSuccessMessage(result.message || "Password changed successfully!");
            setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
            setTimeout(() => {
                setSuccessMessage('');
                setErrorMessage('');
            }, 4000);
        }
    };


    const renderPasswordInput = (label, name, value, error, isVisible, toggle) => (
        <div className="changepassword-form-group">
            <label htmlFor={name}>{label}</label>
            <div className="changepassword-input-wrapper">
                <span className="changepassword-icon"><Lock size={18} /></span>
                <input
                    type={isVisible ? "text" : "password"}
                    id={name}
                    name={name}
                    value={value}
                    onChange={handleChange}
                    className={error ? "changepassword-input-error" : ""}
                />
                <span className="changepassword-eye" onClick={() => toggle(name)}>
                    {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
            </div>
            {error && <span className="changepassword-error-text">{error}</span>}
        </div>
    );

    return (
        <div className="changepassword-container">
            <div className="changepassword-left">
                <h1>Secure Your Account</h1>
                <p>Changing your password regularly helps protect your personal data.</p>
                <ul>
                    <li>Use strong, unique passwords</li>
                    <li>Never share your password</li>
                    <li>Update it often</li>
                </ul>
            </div>

            <div className="changepassword-card">
                <div className="changepassword-header">
                    <h2>Change Password</h2>
                    <p>Please fill in the form to update your password</p>
                </div>

                {successMessage && <div className="changepassword-alert changepassword-success">{successMessage}</div>}
                {errorMessage && <div className="changepassword-alert changepassword-error">{errorMessage}</div>}

                <form className="changepassword-form" onSubmit={handleSubmit}>
                    {renderPasswordInput("Current Password", "currentPassword", formData.currentPassword, formErrors.currentPassword, showPassword.current, togglePasswordVisibility)}
                    {renderPasswordInput("New Password", "newPassword", formData.newPassword, formErrors.newPassword, showPassword.new, togglePasswordVisibility)}
                    {renderPasswordInput("Confirm Password", "confirmPassword", formData.confirmPassword, formErrors.confirmPassword, showPassword.confirm, togglePasswordVisibility)}

                    <div className="changepassword-form-group">
                        <button type="submit" className="changepassword-button" disabled={isSubmitting}>
                            {isSubmitting ? "Changing..." : "Change Password"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ChangePassword;
